import { ReceivableAvailabilitySchema } from '@shared/contracts/receivable-availability'
import { z } from 'zod'
import {
  CurrencySchema,
  LedgerEntrySourceSchema,
  LedgerEntryStatusSchema,
  LedgerEntryTypeSchema,
  ReceivingPeriodSchema,
} from '@shared/contracts/enums'
import type { Currency } from '@shared/contracts/enums'
import { MonetaryValueSchema } from '@shared/lib/money/money'

export const ReleaseSchema = z.object({
  date: z.string(),
  amount: MonetaryValueSchema,
  count: z.number(),
})

export const BalanceSummarySchema = z.object({
  currency: CurrencySchema,
  available: MonetaryValueSchema,
  pending: MonetaryValueSchema,
  awaitingAvailability: MonetaryValueSchema.optional(),
  reserve: MonetaryValueSchema,
  openDebt: MonetaryValueSchema,
  processingWithdrawal: MonetaryValueSchema,
  processingDebtRecovery: MonetaryValueSchema,
  nextReleases: z.array(ReleaseSchema),
})
export const BalanceListSchema = z.array(BalanceSummarySchema)

export const ReleaseItemKindSchema = z.enum([
  'ONE_TIME',
  'SUBSCRIPTION_INITIAL',
  'RENEWAL',
  'RESERVE',
])

export const ReleaseDeductionKindSchema = z.enum([
  'REFUND',
  'PREVENTED_REFUND',
  'CHARGEBACK',
  'CHARGEBACK_FEE',
  'FX_LOSS',
])

export const ReleaseDetailSchema = z.object({
  date: z.string(),
  currency: CurrencySchema,
  computedAt: z.string(),
  summary: z.object({
    grossVolume: MonetaryValueSchema,
    originalProjected: MonetaryValueSchema,
    income: MonetaryValueSchema,
    reserve: MonetaryValueSchema,
    deductions: z.object({
      fxLosses: MonetaryValueSchema.optional(),
      refunds: MonetaryValueSchema,
      preventedRefunds: MonetaryValueSchema,
      chargebacks: MonetaryValueSchema,
      chargebackFees: MonetaryValueSchema,
    }),
    netRelease: MonetaryValueSchema,
    entryCount: z.number(),
    saleCount: z.number(),
  }),
  sources: z.object({
    oneTime: z.object({ count: z.number(), amount: MonetaryValueSchema }),
    subscriptionInitial: z.object({ count: z.number(), amount: MonetaryValueSchema }),
    renewals: z.object({ count: z.number(), amount: MonetaryValueSchema }),
    reserves: z.object({ count: z.number(), amount: MonetaryValueSchema }),
  }),
  items: z.array(
    z.object({
      id: z.string(),
      saleId: z.string(),
      isReferral: z.boolean().optional(),
      invoiceId: z.string().optional(),
      subscriptionId: z.string().optional(),
      kind: ReleaseItemKindSchema,
      buyerName: z.string(),
      canViewSale: z.boolean().optional(),
      productName: z.string(),
      amount: MonetaryValueSchema,
      grossAmount: MonetaryValueSchema.optional(),
      entryCount: z.number(),
      availableAt: z.string(),
      paidAt: z.string().optional(),
    }),
  ),
  adjustments: z.array(
    z.object({
      id: z.string(),
      affectedSaleId: z.string(),
      debtSaleId: z.string(),
      canViewSale: z.boolean().optional(),
      debtId: z.string(),
      kind: ReleaseDeductionKindSchema,
      amount: MonetaryValueSchema,
      occurredAt: z.string(),
    }),
  ),
  hasMore: z.boolean(),
  nextOffset: z.number().nullable(),
})

export const LedgerEntrySchema = z.object({
  id: z.string(),
  saleId: z.string(),
  type: LedgerEntryTypeSchema,
  recipientId: z.string().optional(),
  amount: MonetaryValueSchema,
  availableAt: z.string().nullable(),
  availability: ReceivableAvailabilitySchema.optional(),
  createdAt: z.string(),
  status: LedgerEntryStatusSchema,
  source: LedgerEntrySourceSchema,
  role: z.string().optional(),
  appliedPercentage: z.string().optional(),
  baseAmount: MonetaryValueSchema.optional(),
  withdrawalId: z.string().optional(),
  debtId: z.string().optional(),
  receivingPeriod: ReceivingPeriodSchema.optional(),
  kind: z.string().optional(),
})

export const StatementEventTypeSchema = z.enum([
  'WITHDRAWAL_OBSERVED',
  'SALE_SETTLED',
  'SALE_REVOKED',
  'DEBT_CREATED',
  'DEBT_SETTLED',
  'DEBT_REVERSED',
  'WITHDRAWAL_REQUESTED',
  'WITHDRAWAL_APPROVED',
  'WITHDRAWAL_REFUSED',
  'WITHDRAWAL_COMPLETED',
  'WITHDRAWAL_REVERTED',
  'CONVERSION',
])

export const StatementDirectionSchema = z.enum(['IN', 'OUT', 'HELD', 'INFO'])

export const StatementEntrySchema = z.object({
  id: z.string(),
  recipientId: z.string(),
  occurredAt: z.string(),
  eventType: StatementEventTypeSchema,
  saleId: z.string().optional(),
  relatedSaleIds: z.array(z.string()).catch([]),
  amount: MonetaryValueSchema,
  direction: StatementDirectionSchema,
  currency: CurrencySchema,
  detail: z.record(z.string(), z.unknown()).catch({}),
  eventId: z.string(),
  correlationId: z.string().optional(),
  createdAt: z.string(),
})

export const StatementSchema = z.array(StatementEntrySchema)

export const StatementPageSchema = z.object({
  items: z.array(StatementEntrySchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})

export type StatementEventType = z.infer<typeof StatementEventTypeSchema>
export type StatementDirection = z.infer<typeof StatementDirectionSchema>

export interface StatementQueryParams {
  direction?: 'IN' | 'OUT'
  cursor?: string
  limit?: number
  dateFrom?: string
  dateTo?: string
  currency?: Currency
  q?: string
}

export type StatementPage = z.infer<typeof StatementPageSchema>

export type Release = z.infer<typeof ReleaseSchema>
export type ReleaseDetail = z.infer<typeof ReleaseDetailSchema>
export type ReleaseItemKind = z.infer<typeof ReleaseItemKindSchema>
export type ReleaseDeductionKind = z.infer<typeof ReleaseDeductionKindSchema>
export type BalanceSummary = z.infer<typeof BalanceSummarySchema>
export type LedgerEntry = z.infer<typeof LedgerEntrySchema>
export type StatementEntry = z.infer<typeof StatementEntrySchema>
